// ============================================================================
// NOTE EDITOR MODAL
// Modal para crear o editar una nota interna.
// ============================================================================
import { useState, useEffect, useRef } from 'react';
import { X, Pin } from 'lucide-react';
import { toast } from '../ui/Toast';
import { patientNotesApi } from '../../api/patient-notes';
import type { PatientNote } from '../../types/patient';

interface NoteEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (note: PatientNote) => void;
  patientId: string;
  note?: PatientNote | null;      // si presente → modo edición
}

const MAX_LENGTH = 5000;

export function NoteEditorModal({
  isOpen,
  onClose,
  onSaved,
  patientId,
  note = null,
}: NoteEditorModalProps) {
  const isEdit = !!note;
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      setContent(note?.content ?? '');
      setPinned(note?.pinned ?? false);
      setIsSubmitting(false);
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [isOpen, note]);

  if (!isOpen) return null;

  const canSubmit = content.trim().length > 0 && content.length <= MAX_LENGTH && !isSubmitting;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      let saved: PatientNote;
      if (isEdit && note) {
        saved = await patientNotesApi.update(note.id, { content: content.trim() });
        // El pin se mantiene aparte porque la ruta es distinta.
        if (note.pinned !== pinned) {
          saved = await patientNotesApi.togglePin(note.id, pinned);
        }
        toast.success('Nota actualizada');
      } else {
        saved = await patientNotesApi.create(patientId, {
          content: content.trim(),
          pinned,
        });
        toast.success('Nota creada');
      }
      onSaved(saved);
      onClose();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo guardar la nota');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSubmit();
    }
    if (e.key === 'Escape' && !isSubmitting) {
      onClose();
    }
  };

  const remaining = MAX_LENGTH - content.length;
  const isNearLimit = remaining < 200;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Editar nota' : 'Nueva nota'}
          </h3>
          <button
            type="button"
            onClick={() => !isSubmitting && onClose()}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={7}
              maxLength={MAX_LENGTH}
              placeholder="Escribe la nota..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] focus:border-transparent resize-y"
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between text-xs">
              <label className="inline-flex items-center gap-2 cursor-pointer text-gray-700">
                <input
                  type="checkbox"
                  checked={pinned}
                  onChange={(e) => setPinned(e.target.checked)}
                  className="rounded border-gray-300 text-[#008DD0] focus:ring-[#008DD0]"
                  disabled={isSubmitting}
                />
                <span className="inline-flex items-center gap-1">
                  <Pin className="h-3.5 w-3.5" />
                  Fijar esta nota arriba
                </span>
              </label>
              <span className={isNearLimit ? 'text-amber-600' : 'text-gray-400'}>
                {remaining} caracteres
              </span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={() => !isSubmitting && onClose()}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-[#008DD0] rounded-lg hover:bg-[#0077b0] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear nota'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NoteEditorModal;
